import { Box } from "@mui/material"
import React, { memo, useCallback, useEffect, useId, useRef } from "react"
import { PlayerEvent, playerStore } from "src/store/PlayerStore"

import { useEventBind } from "../../hooks/useEventBind"
import {
  convertTypeToWebSlug,
  usePageviewTracking,
} from "../../screens/Detail/use-pageview-tracking"
import { setSentryTag } from "../../utilities/sentry"
import { ContentType } from "../Sliders/Slider"

import { PlaylistModal } from "./UI/Modals/PlaylistModal"
import { VideoUIWrapper } from "./UI/VideoUI"
import { usePlayer } from "./use-player"
import { usePlayerTracking } from "./use-player-tracking"
import { WebPlayer } from "./WebPlayer"

const MemoizedPlayer = memo(() => {
  return <WebPlayer />
})

export const Player = () => {
  const {
    open,
    type,
    id: contentId,
  } = playerStore((state) => state.navigationProps)
  const setEventListener = playerStore((state) => state.setEventListener)
  const removeEventListener = playerStore((state) => state.removeEventListener)
  const eventListeners = playerStore((state) => state.eventListeners)
  const videoRef = playerStore((state) => state.videoRef)
  const setDuration = playerStore((state) => state.setDuration)
  const track = usePlayerTracking()
  const trackPageview = usePageviewTracking()
  const id = useId()
  const url = playerStore((state) => state.url)
  // prevent double tracking of same event if it hasn't changed
  const lastTrackedState = useRef<"play" | "pause" | undefined>(null)

  const { play } = usePlayer()
  const globalListener = useCallback(
    (type: PlayerEvent, returnVal?: unknown) => {
      const listeners = eventListeners.filter((l) => l.type === type)
      listeners.map((l) => l.callback(returnVal))
    },
    [eventListeners],
  )
  const playCallback = () => globalListener(PlayerEvent.Play)
  useEventBind(videoRef, "play", playCallback)

  const pauseCallback = () => globalListener(PlayerEvent.Pause)
  useEventBind(videoRef, "pause", pauseCallback)

  const timeupdateCallback = () => globalListener(PlayerEvent.Progress)
  useEventBind(videoRef, "timeupdate", timeupdateCallback)

  const durationchangeCallback = () => setDuration(videoRef?.duration ?? 0)
  useEventBind(videoRef, "durationchange", durationchangeCallback)

  const loadCallback = () => globalListener(PlayerEvent.Loading)
  useEventBind(videoRef, "load", loadCallback)

  const waitingCallback = () => globalListener(PlayerEvent.Loading)
  useEventBind(videoRef, "waiting", waitingCallback)

  const loadeddataCallback = () => globalListener(PlayerEvent.Loaded)
  useEventBind(videoRef, "loadeddata", loadeddataCallback)

  const canplayCallback = () => globalListener(PlayerEvent.Loaded)
  useEventBind(videoRef, "canplay", canplayCallback)

  const endedCallback = () => globalListener(PlayerEvent.Ended)
  useEventBind(videoRef, "ended", endedCallback)

  useEffect(() => {
    if (!url) lastTrackedState.current = undefined
    setEventListener(
      PlayerEvent.Play,
      () => {
        if (lastTrackedState.current !== "play") {
          track({ action: "Play" })
          setSentryTag("playing", contentId)
          lastTrackedState.current = "play"
        }
      },
      id,
    )
    setEventListener(
      PlayerEvent.Pause,
      () => {
        if (lastTrackedState.current !== "pause") {
          track({ action: "Pause" })
          setSentryTag("playing", undefined)
          lastTrackedState.current = "pause"
        }
      },
      id,
    )
    setEventListener(
      PlayerEvent.Ended,
      () => {
        track({ action: "Ended" })
        setSentryTag("playing", undefined)
      },
      id,
    )
    return () => {
      removeEventListener(PlayerEvent.Play, id)
      removeEventListener(PlayerEvent.Pause, id)
      removeEventListener(PlayerEvent.Ended, id)
    }
  }, [url])

  useEffect(() => {
    if (open) {
      track({ action: "Start" })
      trackPageview(
        "Player",
        `${convertTypeToWebSlug(type as ContentType)}/${contentId}?player=true`,
      )
      play()
    }
  }, [open, contentId, type])

  return (
    <>
      <MemoizedPlayer />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: "100%",
          width: "100%",
        }}
      >
        {open && <VideoUIWrapper />}
      </Box>
      <PlaylistModal />
    </>
  )
}
